/**
 * ChoicePillGroup — single-select chips for AML options (income, funds, Y/N).
 * Large touch targets, clear selected state, no bottom-sheet friction.
 */
import { View, Text } from 'react-native'
import { Pressable } from '@/components/ui/Pressable'
import { selection } from '@/lib/utils/haptics'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'

interface ChoicePillOption<T extends string | boolean> {
  value: T
  label: string
}

interface ChoicePillGroupProps<T extends string | boolean> {
  label: string
  options: ChoicePillOption<T>[]
  value: T | null
  onChange: (value: T) => void
  error?: string
  helper?: string
  testIDPrefix: string
  /** 2-column grid for longer labels; default wraps naturally */
  columns?: 1 | 2
}

export function ChoicePillGroup<T extends string | boolean>({
  label,
  options,
  value,
  onChange,
  error,
  helper,
  testIDPrefix,
  columns = 1,
}: ChoicePillGroupProps<T>) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View>
      {/* The one label rule this card runs on, copied exactly off the controls
          it sits beside: TextInput, DatePicker and Select all draw their label
          at 14pt FONTS.body, textPrimary held at 0.75, +0.2 tracking, with
          SPACING.md of air before the control.

          Missing the opacity was the last of the three treatments the audit
          counted in one white card — at full textPrimary, "Género" read a
          shade blacker than "Fecha de nacimiento" directly above it, which is
          exactly how a label announces itself as more important than the one
          before it. Nothing here is. */}
      <Text
        style={{
          fontSize: 14,
          letterSpacing: 0.2,
          fontFamily: FONTS.body,
          color: colors.textPrimary,
          opacity: 0.75,
          // A helper belongs to the label, not to the pills: it stays bound to
          // it and the SPACING.md gap moves down to sit above the controls.
          marginBottom: helper ? SPACING.xs : SPACING.md,
        }}
      >
        {label}
      </Text>
      {helper ? (
        <Text
          style={{
            fontSize: 13,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            lineHeight: 18,
            marginBottom: SPACING.md,
          }}
        >
          {helper}
        </Text>
      ) : null}
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: SPACING.sm,
        }}
      >
        {options.map((option) => {
          const selected = value === option.value
          const key = String(option.value)
          return (
            <Pressable
              key={key}
              onPress={() => {
                void selection()
                onChange(option.value)
              }}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={option.label}
              testID={`${testIDPrefix}.${key}`}
              style={{
                minHeight: 44,
                minWidth: columns === 2 ? '47%' : undefined,
                flexGrow: columns === 2 ? 1 : 0,
                paddingHorizontal: SPACING.md,
                paddingVertical: SPACING.sm,
                borderRadius: RADIUS.full,
                borderWidth: 1,
                borderColor: selected ? COLORS.coral.DEFAULT : colors.border,
                backgroundColor: selected
                  ? isDark
                    ? 'rgba(240,66,45,0.20)'
                    : 'rgba(240,66,45,0.10)'
                  : isDark
                    ? colors.surfaceSubtle
                    : colors.background,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text
                numberOfLines={2}
                adjustsFontSizeToFit
                minimumFontScale={0.85}
                style={{
                  fontSize: 14,
                  lineHeight: 18,
                  textAlign: 'center',
                  fontFamily: selected ? FONTS.bodyBold : FONTS.body,
                  color: selected ? colors.textAccent : colors.textPrimary,
                }}
              >
                {option.label}
              </Text>
            </Pressable>
          )
        })}
      </View>
      {error ? (
        <Text
          style={{
            fontSize: 12,
            fontFamily: FONTS.body,
            color: colors.textError,
            marginTop: SPACING.sm,
          }}
        >
          {error}
        </Text>
      ) : null}
    </View>
  )
}
